/**
 * Centralized Storage Constants for BiteWise Healthcare Platform
 *
 * This file contains all localStorage and sessionStorage keys used throughout
 * the application. Centralizing these constants improves maintainability,
 * reduces duplication, and provides better type safety.
 */

// Storage key prefix for namespace isolation
const STORAGE_PREFIX = 'bitewise' as const;

// Helper function to create namespaced storage keys
const createStorageKey = (key: string): string => `${STORAGE_PREFIX}_${key}`;

/**
 * Authentication Storage Keys
 */
export const AUTH_STORAGE = {
  TOKEN: 'authToken', // Keep existing key for backward compatibility
} as const;

/**
 * Provider Registration Workflow Storage Keys
 */
export const PROVIDER_REGISTRATION_STORAGE = {
  WORKFLOW: createStorageKey('provider_registration_workflow'),
  REGISTRATION_FORM: createStorageKey('provider_registration_form'),
  PROFILE_FORM: createStorageKey('provider_profile_form'),
} as const;

/**
 * Dietician Registration Workflow Storage Keys
 */
export const DIETICIAN_REGISTRATION_STORAGE = {
  WORKFLOW: createStorageKey('dietician_registration_workflow'),
} as const;

/**
 * Patient Registration Workflow Storage Keys
 */
export const PATIENT_REGISTRATION_STORAGE = {
  WORKFLOW: createStorageKey('patient_registration_workflow'),
} as const;

/**
 * Referral Workflow Storage Keys
 */
export const REFERRAL_STORAGE = {
  WORKFLOW: createStorageKey('referral_workflow'),
  TOKEN: createStorageKey('referral_token'),
} as const;

/**
 * Theme Management Storage Keys
 */
export const THEME_STORAGE = {
  PREFERENCE: createStorageKey('theme_preference'),
} as const;

/**
 * All Storage Keys - Union type for type safety
 */
export type StorageKey =
  | typeof AUTH_STORAGE[keyof typeof AUTH_STORAGE]
  | typeof PROVIDER_REGISTRATION_STORAGE[keyof typeof PROVIDER_REGISTRATION_STORAGE]
  | typeof DIETICIAN_REGISTRATION_STORAGE[keyof typeof DIETICIAN_REGISTRATION_STORAGE]
  | typeof PATIENT_REGISTRATION_STORAGE[keyof typeof PATIENT_REGISTRATION_STORAGE]
  | typeof REFERRAL_STORAGE[keyof typeof REFERRAL_STORAGE]
  | typeof THEME_STORAGE[keyof typeof THEME_STORAGE];

/**
 * Storage Categories for organized access
 */
export const STORAGE_CATEGORIES = {
  AUTH: AUTH_STORAGE,
  PROVIDER_REGISTRATION: PROVIDER_REGISTRATION_STORAGE,
  DIETICIAN_REGISTRATION: DIETICIAN_REGISTRATION_STORAGE,
  PATIENT_REGISTRATION: PATIENT_REGISTRATION_STORAGE,
  REFERRAL: REFERRAL_STORAGE,
  THEME: THEME_STORAGE,
} as const;